import { updateCartWithSizeQuantities as updateCartWithSizeQuantitiesUtil } from '../util/cart';
import { storableError } from '../util/errors';
import { updateCurrentUserProfile } from './user.duck';

// ================ Action types ================ //

export const UPDATE_CART_WITH_SIZE_QUANTITIES_REQUEST =
  'app/cart/UPDATE_CART_WITH_SIZE_QUANTITIES_REQUEST';
export const UPDATE_CART_WITH_SIZE_QUANTITIES_SUCCESS =
  'app/cart/UPDATE_CART_WITH_SIZE_QUANTITIES_SUCCESS';
export const UPDATE_CART_WITH_SIZE_QUANTITIES_ERROR =
  'app/cart/UPDATE_CART_WITH_SIZE_QUANTITIES_ERROR';

export const SET_CART = 'app/cart/SET_CART';
export const CLEAR_CART = 'app/cart/CLEAR_CART';

export const LOAD_CART_FROM_STORAGE = 'app/cart/LOAD_CART_FROM_STORAGE';
export const SAVE_CART_TO_STORAGE = 'app/cart/SAVE_CART_TO_STORAGE';

// ================ Reducer ================ //

const initialState = {
  cart: {},
  updateCartWithSizeQuantitiesInProgressIds: [],
  updateCartWithSizeQuantitiesError: null,
};

const removeListingsFromCart = (cart, listingIds) => {
  return Object.keys(cart).reduce((acc, providerId) => {
    const providerCart = cart[providerId];
    const newProviderCart = Object.keys(providerCart).reduce((acc, listingId) => {
      if (!listingIds.includes(listingId)) {
        acc[listingId] = providerCart[listingId];
      }
      return acc;
    }, {});
    if (Object.keys(newProviderCart).length > 0) {
      acc[providerId] = newProviderCart;
    } else {
      delete acc[providerId];
    }
    return acc;
  }, {});
};

const cartReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case UPDATE_CART_WITH_SIZE_QUANTITIES_REQUEST:
      return {
        ...state,
        updateCartWithSizeQuantitiesInProgressIds: [
          ...state.updateCartWithSizeQuantitiesInProgressIds,
          payload,
        ],
        toggleCartError: null,
      };

    case UPDATE_CART_WITH_SIZE_QUANTITIES_SUCCESS:
      return {
        ...state,
        updateCartWithSizeQuantitiesInProgressIds: state.updateCartWithSizeQuantitiesInProgressIds.filter(
          id => id !== payload
        ),
      };

    case UPDATE_CART_WITH_SIZE_QUANTITIES_ERROR:
      return {
        ...state,
        updateCartWithSizeQuantitiesInProgressIds: state.updateCartWithSizeQuantitiesInProgressIds.filter(
          id => id !== payload
        ),
        toggleCartError: payload,
      };

    case SET_CART:
      return {
        ...state,
        cart: payload,
      };

    case CLEAR_CART:
      const { cart } = state;
      if (!payload?.length) {
        return {
          ...state,
          cart: {},
        };
      }
      const newCart = removeListingsFromCart(cart, payload);

      return {
        ...state,
        cart: newCart,
      };

    case LOAD_CART_FROM_STORAGE:
      return {
        ...state,
        cart: payload,
      };

    case SAVE_CART_TO_STORAGE:
      return {
        ...state,
        cart: payload,
      };

    default:
      return state;
  }
};

export default cartReducer;

// ================ Action creators ================ //

export const updateCartWithSizeQuantitiesRequest = listingId => ({
  type: UPDATE_CART_WITH_SIZE_QUANTITIES_REQUEST,
  payload: listingId,
});

export const updateCartWithSizeQuantitiesSuccess = listingId => ({
  type: UPDATE_CART_WITH_SIZE_QUANTITIES_SUCCESS,
  payload: listingId,
});

export const updateCartWithSizeQuantitiesError = error => ({
  type: UPDATE_CART_WITH_SIZE_QUANTITIES_ERROR,
  error: true,
  payload: error,
});

export const setCart = cart => ({
  type: SET_CART,
  payload: cart,
});

export const clearCart = listingIds => ({
  type: CLEAR_CART,
  payload: listingIds,
});

// ================ Thunks ================ //

/**
 * Update cart with multiple sizes and quantities for a single listing
 * This is used when the user selects multiple sizes with different quantities
 */
export const updateCartWithSizeQuantities = (
  providerId,
  listingId,
  orderedSizeWithQuantities
) => async (dispatch, getState) => {
  try {
    dispatch(updateCartWithSizeQuantitiesRequest(listingId));
    const oldCart = getState().cart?.cart || {};
    const currentUser = getState().user?.currentUser;
    const isAuthenticated = !!currentUser?.id;

    let newCart = oldCart;

    if (isAuthenticated) {
      // For authenticated users, update cart in user profile
      newCart = updateCartWithSizeQuantitiesUtil(
        oldCart,
        providerId,
        listingId,
        orderedSizeWithQuantities
      );

      // Update user profile with new cart
      await dispatch(updateCurrentUserProfile('privateData', { cart: newCart }));
    } else {
      // For unauthenticated users, save to localStorage
      newCart = updateCartWithSizeQuantitiesUtil(
        oldCart,
        providerId,
        listingId,
        orderedSizeWithQuantities
      );
      localStorage.setItem('cart', JSON.stringify(newCart));
    }

    dispatch(updateCartWithSizeQuantitiesSuccess(listingId));
    dispatch(setCart({ ...newCart }));

    return newCart;
  } catch (error) {
    const storableErr = storableError(error);
    dispatch(updateCartWithSizeQuantitiesError(storableErr));
    throw error;
  }
};

/**
 * Load cart from localStorage
 */
export const loadCartFromLocalStorage = () => dispatch => {
  if (typeof window !== 'undefined') {
    const cart = localStorage.getItem('cart');
    if (cart) {
      try {
        const parsedCart = JSON.parse(cart);
        dispatch(setCart(parsedCart));
        return parsedCart;
      } catch (error) {
        console.error('Error parsing cart from localStorage:', error);
        return {};
      }
    }
  }
  return {};
};

/**
 * Save cart to localStorage
 */
export const saveCartToLocalStorage = cart => dispatch => {
  if (typeof window !== 'undefined') {
    try {
      localStorage.setItem('cart', JSON.stringify(cart));
      dispatch(setCart(cart));
    } catch (error) {
      console.error('Error saving cart to localStorage:', error);
    }
  }
};

/**
 * Initialize cart (load from localStorage or user profile)
 */
export const initializeCart = () => (dispatch, getState) => {
  const currentUser = getState().user?.currentUser;
  const isAuthenticated = !!currentUser?.id;

  if (isAuthenticated) {
    // For authenticated users, cart should be loaded from user profile
    // This is typically handled in the user duck when fetching current user
    const cart = currentUser?.attributes?.profile?.privateData?.cart;
    if (cart) {
      dispatch(setCart(cart));
      return cart;
    }
  } else {
    // For unauthenticated users, load from localStorage
    return dispatch(loadCartFromLocalStorage());
  }

  return {};
};
